import InstagramIcon from '@mui/icons-material/Instagram';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import _ from 'lodash';
import {
  AutocompleteInput,
  ChipField,
  Datagrid,
  Filter,
  FunctionField,
  List,
  ReferenceInput,
  SingleFieldList,
  TextField,
  TextInput,
  useGetList,
} from 'react-admin';
import { Else, If, Then, Unless, When } from 'react-if';

import RecordOutreachButton from './Components/RecordOutreachButton';
import type {
  DropDownOption,
  IndustryAlbumCredits,
  IndustryContributor,
  IndustryPlaylistAlbum,
} from '@/Api/studioServerTypes';
import { getResourceFilter } from '@/constants';
import { industryRoleChoices } from '@/Helper/DropdownChoices/dropdownChoices';
import { stringAvatar } from '@/Helper/stringAvatar';

function IndustryContributorFilter(props: any): JSX.Element {
  const { data: industryGenreChoices = [] } = useGetList<DropDownOption>('industry-contributors/list/industry-genres', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  return (
    <Filter {...props}>
      <TextInput
        label='Name' source='Name'
        alwaysOn
      />
      <TextInput
        label='City' source='City'
        alwaysOn
      />

      <ReferenceInput
        label='Assigned To'
        source='AssignedResourceIdentifier'
        reference='resources'
        alwaysOn
        sort={{ field: 'Name', order: 'ASC' }}
        filter={{ ...getResourceFilter() }}
      >
        <AutocompleteInput
          optionText='Name'
          label='Assigned To'
          filterToQuery={(searchText: string): { Name: string } => ({ Name: searchText })}
        />
      </ReferenceInput>

      <AutocompleteInput
        label='Role'
        source='role'
        alwaysOn
        choices={industryRoleChoices}
        optionText='name'
        optionValue='id'
        sx={{ minWidth: 260 }}
      />

      <AutocompleteInput
        label='Genre'
        source='genre'
        alwaysOn
        choices={industryGenreChoices}
        optionText='name'
        optionValue='id'
        sx={{ minWidth: 260 }}
      />

      {/* Commented Out Until Further Notice */}
      {/* <BooleanInput
        label='Sterling Sound' source='IsSterling'
        alwaysOn
      /> */}
    </Filter>
  );
}

export default function IndustryInsightsList(props: any): JSX.Element {
  return (
    <List
      {...props}
      filters={<IndustryContributorFilter />}
      perPage={25}
      sort={{ field: 'AlbumCount', order: 'DESC' }}
    >
      <Datagrid
        rowClick='edit'
        bulkActionButtons={false}
        sx={{
          '& .RaDatagrid-rowCell': { verticalAlign: 'center', whiteSpace: 'nowrap' },
          '& .RaDatagrid-rowCell.roles, & .RaDatagrid-rowCell.genres': {
            whiteSpace: 'normal',
            maxWidth: 360,
          },
        }}
      >
        <TextField
          source='Name' label='Name'
        />
        <FunctionField
          label='City'
          sortable={false}
          render={(record: IndustryContributor): string => (record?.ContactIdentifierNavigation?.City ?? '—').trim() || '—'}
        />

        <FunctionField
          label='Roles'
          sortable={false}
          cellClassName='roles'
          render={(record: IndustryContributor): JSX.Element => {
            const allowedRoleIds = new Set(industryRoleChoices.map((c) => c.id));

            const rows = _((record?.IndustryAlbumCredits ?? []) as (IndustryAlbumCredits | null)[])
              .filter(Boolean)
              .map((c) => ({
                role: (c!.Role ?? '').trim(),
                albumId: (c!.IndustryAlbumIdentifier ?? '').trim(),
              }))
              .filter(({ role, albumId }) => !!albumId && allowedRoleIds.has(role))
              .groupBy('role')
              .map((items, role) => ({
                role,
                count: _(items).map('albumId').uniq()
                  .size(),
              }))
              .orderBy(['count', 'role'], ['desc', 'asc'])
              .map(({ role, count }) => ({
                id: role,
                name: count > 1 ? `${role} (${count})` : role,
              }))
              .value();

            if (!rows.length) return <>—</>;

            return (
              <SingleFieldList linkType={false} data={rows}>
                <ChipField source='name' size='small' />
              </SingleFieldList>
            );
          }}
        />

        <FunctionField
          label='Genres'
          sortable={false}
          cellClassName='genres'
          render={(record: IndustryContributor): JSX.Element => {
            const genres = _((record?.IndustryAlbumCredits ?? []) as (IndustryAlbumCredits | null)[])
              .filter(Boolean)
              .flatMap((c) => c!.IndustryAlbumIdentifierNavigation?.IndustryPlaylistAlbums ?? [])
              .filter(Boolean)
              .map((pa: IndustryPlaylistAlbum) => (pa.IndustryPlaylistIdentifierNavigation?.Genre ?? '').trim())
              .filter(Boolean)
              .uniq()
              .value();

            if (!genres.length) return <>—</>;

            return (
              <SingleFieldList
                linkType={false}
                data={genres.map((name, i) => ({ id: i, name }))}
              >
                <ChipField source='name' size='small' />
              </SingleFieldList>
            );
          }}
        />

        <FunctionField
          label='Sterling'
          sortBy='SterlingAlbumCount'
          render={(record: IndustryContributor & { SterlingAlbumCount: number }) => (
            <If condition={(record?.SterlingAlbumCount ?? 0) > 0}>
              <Then>
                <ChipField
                  source='SterlingAlbumCount'
                  record={{ SterlingAlbumCount: record.SterlingAlbumCount }}
                  size='small'
                />
              </Then>
              <Else>-</Else>
            </If>
          )}
        />

        <FunctionField
          label='Albums'
          sortBy='AlbumCount'
          render={(record: IndustryContributor & { AlbumCount: number }) => (
            <>
              <When condition={(record?.AlbumCount ?? 0) > 0}>
                <ChipField
                  source='AlbumCount'
                  record={{ AlbumCount: record.AlbumCount }}
                  size='small'
                />
              </When>

              <Unless condition={(record?.AlbumCount ?? 0) > 0}>
                —
              </Unless>
            </>
          )}
        />

        <FunctionField
          label='AssignedTo'
          sortable={false}
          render={(record: IndustryContributor): JSX.Element | string => {
            const assignedToName = (record?.AssignedResourceIdentifierNavigation?.Name ?? '').trim();
            if (!assignedToName) return '—';
            const avatarProps = stringAvatar(assignedToName);

            return (
              <Chip
                size='small'
                label={assignedToName}
                avatar={(
                  <Avatar
                    {...avatarProps}
                    sx={{
                      ...avatarProps.sx,
                      width: 22,
                      height: 22,
                      fontSize: 11,
                    }}
                  />
                )}
              />
            );
          }}
        />

        <FunctionField
          label='Instagram'
          sortable={false}
          render={(record: IndustryContributor): JSX.Element => {
            const href = record?.ContactIdentifierNavigation?.InstagramUrl?.trim();
            if (!href) return <>—</>;

            return (
              <Tooltip title='Open Instagram' arrow>
                <IconButton
                  size='small'
                  component='a'
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  onClick={(e) => e.stopPropagation()}
                  aria-label='Open Instagram'
                >
                  <InstagramIcon fontSize='medium' />
                </IconButton>
              </Tooltip>
            );
          }}
        />

        <FunctionField
          label='Last Outreach'
          sortable={false}
          render={(record: IndustryContributor): string => {
            if (!record?.LatestOutreachDate) return '—';
            const utcDate = `${record.LatestOutreachDate.replace(' ', 'T')}Z`;

            return formatDistanceToNowStrict(parseISO(utcDate), { addSuffix: true });
          }}
        />

        <FunctionField
          label='Outreach'
          sortable={false}
          render={(): JSX.Element => (
            <RecordOutreachButton
              stopPropagation
            />
          )}
        />

      </Datagrid>
    </List>
  );
}
